/**
 * As regras PURAS sobre uma parte de tool — o que ela desenha, e com quê.
 *
 * Vivem fora do componente porque três superfícies decidem sobre as mesmas
 * partes: a bolha do chat (`chat-message.vue`), o drawer do Especialista
 * Fee-Based e a conversa do Guia. Enquanto moravam só na bolha, as duas
 * superfícies do Fee Fixo simplesmente NÃO desenhavam cartão nenhum — o modelo
 * oferecia uma ação, o `upsertToolPart` acontecia, e a tela não mostrava nem o
 * botão nem o motivo da recusa ([I-1] do review do PR #2086).
 */

import { LEGACY_TOOL_TO_UI, semPrefixoDeMcp } from '@modules/aawz-intelligence-v2/ui-registry'
import { EXECUTOR_CLIENTE } from '@modules/aawz-intelligence-v2/ui-registry/chave-de-tool-de-front'
import { TOOL_PART_STATE } from '@modules/aawz-intelligence-v2/store/toolParts'

// Chaves do `contracts/ui-components.md` §3 com dono FORA do registry.
export const CHAVE_PAINEL = 'consultor/painel-deliberacao'
export const CHAVE_SERVICOS = 'crm/servicos-citados'
export const CHAVE_PERGUNTAS = 'crm/perguntas-em-aberto'
/** Continua exportada: é a chave do cartão, só não é mais gate. */
export const CHAVE_CONFIRMACAO = 'crm/confirmacao'

/**
 * As TRÊS chaves cujo dono é um computed, e não o renderer.
 *
 * Elas carregam regras que NÃO estão no payload da tool: a persona decide se
 * serviços aparecem (AC-4.1), o card de perguntas só existe na última mensagem
 * (spec-007 R5) e o painel exige mesa ou turno. Uma parte de `tool_calls` que
 * resolva para uma delas é DESCARTADA — senão a mesma mensagem teria duas
 * origens para o mesmo cartão.
 *
 * ★ `crm/confirmacao` SAIU daqui ([spec-010 T018] / US-4). Ela estava na lista
 *   porque preview e commit são a mesma tool com o mesmo nome, e o
 *   `resolverEntrada` (que vê chave, flag e estado, nunca o `value`) não os
 *   distinguia. O que mudou foi o ESTADO: o backend emite
 *   `tool-output-available` com `state: 'approval-requested'` para o preview e
 *   `output-available` para o commit (`core/sse.py:_mapear_tool_result`) — o
 *   estado separa os dois, e a proposta passa a ter dono no thread, como
 *   qualquer outro cartão. Com isso o cartão sobrevive ao fim do turno e ao
 *   reload, o que o `pendingConfirmation` nunca fez.
 */
export const CHAVES_DE_GATE_LEGADO = [
  CHAVE_PAINEL,
  CHAVE_SERVICOS,
  CHAVE_PERGUNTAS,
]

// Quatro dos cinco `ToolState` do contrato (`store/toolParts.js`).
export const ESTADO_ESQUELETO = TOOL_PART_STATE.ENTRADA
export const ESTADO_APROVACAO = TOOL_PART_STATE.APROVACAO
export const ESTADO_RESOLVIDO = TOOL_PART_STATE.SAIDA
export const ESTADO_ERRO = TOOL_PART_STATE.ERRO

// A falha de uma tool que não desenha cartão nenhum (G-4 da VERIFY it.2).
export const TITULO_TOOL_FALHOU = 'A ação não pôde ser concluída'

/**
 * ★ [T075] O título da RECUSA. Não diz que algo se quebrou, porque nada se
 * quebrou: a tool respondeu, e a resposta foi "não" — com motivo.
 */
export const TITULO_TOOL_RECUSOU = 'Não deu para fazer isso'
export const PREFIXO_TOOL_FALHOU = 'Não foi possível executar'
export const SEM_TEXTO_DE_ERRO = 'a ferramenta falhou sem dizer o motivo'

/**
 * A chave do registry desta parte, ou `null` quando ela não desenha nada.
 *
 * `ui.component` tem precedência sobre o mapa legado: o backend sabe melhor que
 * a tabela do front. Parte sem chave nenhuma não vira cartão NEM fallback — a
 * maioria das tools não desenha, e um "não consegui montar" por tool call
 * encheria o thread de erro onde nunca houve cartão.
 */
export function chaveDaParte(parte) {
  const ui = parte && parte.ui
  if (ui && ui.component) return ui.component
  const tool = parte && parte.tool
  // ★ [spec-008 T082] `semPrefixoDeMcp` aqui TAMBÉM, e não por simetria: este
  // é um SEGUNDO juiz da mesma pergunta ("qual cartão é desta tool?"), lido
  // por `chat-message.vue::partesDeTools`, que descarta a parte inteira com
  // `if (!chave) return false`. Consertar só o `resolverEntrada` deixaria o
  // cartão resolvido no registry e a parte jogada fora antes de chegar nele.
  //
  // O defeito é medido: na sessão paga do G7 (19/09) o MESMO pedido veio como
  // `create_tarefa` numa repetição e `mcp__aawz__create_tarefa` na outra; na
  // segunda não houve cartão, 86 botões e nenhum "Confirmar", com a proposta
  // viva em `approval-requested`.
  const doMapa = tool ? LEGACY_TOOL_TO_UI[semPrefixoDeMcp(tool)] : null
  if (doMapa) return doMapa
  return null
}

/**
 * A parte FALHOU e ninguém mais vai desenhar essa falha (G-4 da VERIFY it.2).
 *
 * Três casos, e nos três o thread ficaria mudo sobre o erro:
 *
 * - **sem chave** — a tool não desenha cartão nenhum (a maioria não desenha);
 * - **chave de gate legado** — quem desenha é um computed, e um turno que
 *   falhou não alimenta nenhum deles. Foi o caso medido pelo QA:
 *   `convocar_painel` recusou o assunto, a parte foi a `output-error` e a tela
 *   mostrou só "Pensando…".
 * - **chave ignorada pela superfície** ([I-11]) — quem desenha é a própria
 *   tela, a partir de uma fatia da store que o turno FALHO não preenche. É o
 *   mesmo raciocínio do gate legado, com a lista vindo do consumidor.
 *
 * @param {Object}   parte
 * @param {string[]} [chavesIgnoradas] As chaves com dono fora do renderizador
 *   NESTA superfície. Ausente ⇒ nenhuma (é o caso da bolha do chat, que tem a
 *   própria lista em `CHAVES_DE_GATE_LEGADO`).
 */
export function falhaSemCartao(parte, chavesIgnoradas) {
  if (!parte || parte.state !== ESTADO_ERRO) return false
  const chave = chaveDaParte(parte)
  if (!chave) return true
  if (CHAVES_DE_GATE_LEGADO.indexOf(chave) !== -1) return true
  return (chavesIgnoradas || []).indexOf(chave) !== -1
}

/**
 * As partes que ESTA superfície deve desenhar, tiradas as que ela já desenha
 * por conta própria ([I-11] do review de front it.2 do PR #2086).
 *
 * ★ **A falha nunca é descartada.** Parte em `output-error` passa mesmo com a
 * chave na lista: o canal legado que desenharia o cartão é alimentado pelo
 * `tool_result` de SUCESSO, então um turno que falhou não põe nada na tela — e
 * o silêncio sobre o erro é justamente o defeito que o G-4 fechou. Ela vira o
 * cartão de falha, pelo `falhaSemCartao` acima.
 *
 * Parte sem chave também passa: ela não colide com cartão nenhum, e é dela que
 * vive o [I-1] nestas telas (a ação oferecida, a recusa do guard).
 */
export function descartarChavesIgnoradas(partes, chavesIgnoradas) {
  const ignoradas = chavesIgnoradas || []
  if (!ignoradas.length) return partes || []
  return (partes || []).filter(function (parte) {
    if (parte && parte.state === ESTADO_ERRO) return true
    const chave = chaveDaParte(parte)
    if (!chave) return true
    return ignoradas.indexOf(chave) === -1
  })
}

/**
 * A parte veio do `tool_calls[]` de uma mensagem, não do turno em andamento.
 *
 * Quem carimba é o `parteDaChamada` (`renderiza-partes.js`), e a distinção é o
 * que separa os dois momentos da MESMA proposta: no turno vivo ela é a decisão
 * que o consultor ainda vai tomar (com os dois botões); na mensagem ela é
 * história — e história não se autoriza de novo.
 */
export function partePersistida(parte) {
  return Boolean(parte && parte.persistida)
}

/**
 * A proposta PERSISTIDA — o preview que voltou no `tool_calls[]` ([G-1] it.5).
 *
 * ★ O discriminante é o `state`, NUNCA a ausência de payload. Enquanto a régua
 * era "sem `output`", ela não alcançava nada do que o backend corrigido grava:
 * ele passou a gravar `output` e `state` JUNTOS (medido em 27/08 no Postgres
 * local, conversas `e3f443c9…` e `ab988d98…` do QA — o preview persiste
 * `{resumo, entidade, operacao, entidade_label, operacao_label,
 * requer_confirmacao}`, que ainda por cima PASSA no schema). Resultado medido:
 * a conversa reaberta oferecia "Cancelar" e "Confirmar" vivos sobre uma escrita
 * já resolvida — o mesmo defeito que a it.2 fechou, por outra porta.
 *
 * Vale só para o cartão de confirmação: os outros cartões desenham o próprio
 * payload, e um selo por cima deles apagaria conteúdo que ninguém pediu para
 * esconder.
 */
export function propostaPersistidaSemDesfecho(parte, chave) {
  if (!partePersistida(parte)) return false
  if (chave !== CHAVE_CONFIRMACAO) return false
  return Boolean(parte) && parte.state === ESTADO_APROVACAO
}

/**
 * O COMMIT de uma write-tool: o `output` é o REGISTRO gravado, não props.
 *
 * O que o backend persiste no commit é a linha da tabela (`{id, title,
 * due_date, id_folder, changelog_id, id_escritorio, …}` de `pm_task`, medido na
 * conversa `e3f443c9…`) — nenhuma das seis chaves que
 * `ui-crm-confirmacao.schema.json` exige. Validá-lo contra o schema do cartão
 * reprovava SEMPRE e trazia de volta o "dados fora do contrato", agora sobre a
 * escrita que deu certo. O cartão desenha o RECIBO pelo estado; o registro é
 * dado, não desenho.
 *
 * ⚠️ `ui.props` vence: se um dia o backend mandar o cartão pronto no commit, é
 * ele que manda — a exceção existe só para o `output` cru.
 */
export function escritaConcluidaSemProps(parte, chave) {
  if (chave !== CHAVE_CONFIRMACAO) return false
  if (!parte || parte.state !== ESTADO_RESOLVIDO) return false
  const ui = parte.ui
  return !(ui && ui.props)
}

/**
 * A parte não tem PAYLOAD nenhum NEM estado para contar (guarda do [G-1]).
 *
 * Sobrou para a chamada ANTIGA: gravada quando o backend ainda registrava só
 * `{id, name, input, executor}`, sem `output` (write-tool nunca entrou em
 * `PERSISTE_OUTPUT_TOOL_NAMES`) e sem `state`. Ali `propsDaParte` devolve `{}`,
 * o schema reprova e a bolha voltava com um `fallback-card` de "dados fora do
 * contrato" — com um **Cancelar órfão** sobre uma escrita já resolvida (medido:
 * `QA2_US-3_confirmacao-apos-cancelar-reaberta.png`).
 *
 * O `fallback-card` é para CONTRATO VIOLADO: o backend mandou `ui` e os props
 * não batem. Aqui ele não mandou nada — não há erro a relatar, há ausência.
 *
 * ★ **E por que ela continua CALADA, enquanto a proposta com `state` vira
 * selo.** Sem o estado, a ausência de `output` não distingue a proposta
 * recusada da que foi gravada; qualquer frase ali inventaria um fato. Com o
 * estado no registro, a parte tem história própria — é o que
 * `propostaPersistidaSemDesfecho` e `escritaConcluidaSemProps` leem.
 *
 * ⚠️ `output-error` NÃO entra aqui: a falha tem dono (`falhaSemCartao`) e
 * continua visível.
 */
export function semPayloadParaDesenhar(parte) {
  if (!parte || parte.state === ESTADO_ERRO) return false
  // A proposta persistida tem o ESTADO, e o estado é a história dela: vira
  // selo, não silêncio (`propostaPersistidaSemDesfecho`, acima).
  if (propostaPersistidaSemDesfecho(parte, chaveDaParte(parte))) return false
  const ui = parte.ui
  if (ui && (ui.component || ui.props)) return false
  return parte.value === undefined || parte.value === null
}

/**
 * A parte que o FIO acabou de abrir e ainda não respondeu (11/09/2026).
 *
 * `tool-input-available` cria a parte em `input-available` sem `ui` e sem
 * `value` — a tool foi chamada, o resultado não existe. Até 11/09 ela virava
 * cartão em esqueleto (ou, atrás do `CartaoPendente`, um "Montando a
 * resposta..." genérico), empilhado sobre o indicador de fase que já dizia
 * "Consultando cliente..." no rodapé da bolha. Dois avisos para o mesmo fato.
 *
 * Hoje ela NÃO desenha: quem conta que a tool está rodando é o rótulo único do
 * turno (`utils/rotulo-do-turno.js`), e o cartão entra — animado — quando o
 * `tool-output-available` chega. O esqueleto de cartão ficou só para a janela
 * de VALIDAÇÃO do schema (`renderiza-partes.js::montarCartao`), que é
 * imediatamente antes do render real.
 *
 * ⚠️ A tool de FRONT fica de fora: `_tratarToolDeFront` a deixa em
 * `input-available` de propósito — é o estado em que o cartão da ação
 * oferecida mostra o botão "Abrir" (`acao-oferecida-card`), e `ui` já veio
 * montado pelo próprio front. A cópia PERSISTIDA também: `parteDaChamada`
 * nunca produz `input-available` para tool de servidor.
 */
export function esqueletoDoFio(parte) {
  if (!parte || parte.state !== ESTADO_ESQUELETO) return false
  if (parte.executor === EXECUTOR_CLIENTE) return false
  return !partePersistida(parte)
}

// `ui.props` é o contrato novo; `value` é o `tool_result` de sempre — é o que
// deixa a tool que ainda não declara `ui` desenhar pelo mapa legado.
export function propsDaParte(parte) {
  const ui = parte && parte.ui
  if (ui && ui.props) return ui.props
  return (parte && parte.value) || {}
}

// O veredito é guardado por parte E por estado: `approval-requested` e
// `output-available` levam props diferentes, e os `output-*` são terminais —
// então cada parte valida uma vez por desfecho, não a cada token do stream.
export function chaveDaValidacao(chave, parte) {
  return (parte.toolCallId || chave) + '|' + parte.state
}

/**
 * ★ [T075] A tool RECUSOU com motivo — e o motivo é melhor que o erro técnico.
 *
 * **Medido em produção (18/09/2026, escritório 83).** O consultor pediu uma
 * simulação para o cliente "JG - Aawz" (11495), que existe e é dele, mas está
 * **sem carteira**. A tool recusou, e recusou CERTO — o contrato dela diz que
 * "não estima; cliente sem carteira lida recebe recusa nomeando o que falta":
 *
 *     [FEE_FIXO][ESTADO_CLIENTE][83][65515] Pedido (id=11495) não casou com
 *     nenhum cadastro da carteira visível. Impacto: recusa, sem abrir esteira.
 *     [AGENTE][PROPOSTAS][83] Preview de 'criar_simulacao_fee_fixo' devolveu
 *     status=404 — cartao nao publicado.
 *
 * Só que o mapa tool→cartão é por NOME, então o front tentava desenhar o
 * `fee-fixo/simulacao-card` assim mesmo, o schema reprovava o envelope de
 * recusa, e o consultor lia **"dados fora do contrato"**. Ele deveria ter lido
 * *"não encontrei carteira para o JG - Aawz"*. A informação existia, era boa —
 * status, cliente, o que faltou — e era jogada fora na última curva.
 *
 * ★ **Ausência de cartão não é violação de contrato.** `fallback-card` é para
 * quando o backend MANDOU um cartão e os props não batem. Recusa nomeada é
 * outra coisa: é resposta.
 *
 * Devolve a mensagem da recusa, ou `''` quando o payload não é um envelope de
 * recusa. Detecção POSITIVA: `status` numérico ≥ 400 **e** `message` com
 * texto — payload de cartão nenhum tem esse par.
 */
export function recusaDaTool(parte) {
  const props = propsDaParte(parte)
  if (!props || typeof props !== 'object') return ''
  const status = props.status
  if (typeof status !== 'number' || status < 400) return ''
  const mensagem = typeof props.message === 'string' ? props.message.trim() : ''
  return mensagem
}

/**
 * ★ [T075] O dado não coube no evento e foi para FORA — isto é um PONTEIRO,
 * não um cartão.
 *
 * `usecases/agente/_tool_result_grande_apoio.py` troca o `value` por
 * `{truncado, preview, id_objeto, chave, bytes}` quando ele passa de
 * `LIMITE_VALUE_TOOL_RESULT_BYTES` (8192). Medido em produção: a
 * `priorizar_carteira_fee_fixo` devolveu 15.672 bytes e o evento saiu sem
 * `ui` — o front tentava validar o ponteiro contra o schema do cartão e
 * chamava de "dados fora do contrato" o que é, na verdade, dado grande demais.
 *
 * ⚠️ O conserto de verdade é no BACKEND (montar o `ui` reduzido — o
 * `core/ui_payload._aplicar_teto` já sabe fazer isso, só não é chamado neste
 * caminho) e está com a sessão que cuida do transporte. Aqui só se para de
 * mentir sobre a causa.
 */
export function payloadDeOffload(parte) {
  const props = propsDaParte(parte)
  if (!props || typeof props !== 'object') return false
  return props.truncado === true && typeof props.chave === 'string' && typeof props.bytes === 'number'
}

/** A chave do cartão que desenha um resultado grande demais para o evento. */
export const CHAVE_RESULTADO_BRUTO = 'chat/resultado-bruto'

/**
 * ★ [T075] O PONTEIRO de offload virado parte com cartão.
 *
 * Em vez de reprovar o ponteiro contra o schema de um cartão que ele nunca
 * seria, monta-se a parte que o `chat/resultado-bruto` sabe desenhar: título,
 * tamanho real e o trecho que veio. O caminho depois disso é o de sempre —
 * `resolverEntrada` acha a chave pelo `ui.component`, o schema aprova, e o
 * painel de artefato recebe a entrega (`abrirNoPainelAutomaticamente`).
 *
 * ⚠️ Não inventa dado: os três campos saem do próprio ponteiro. O `bytes` é o
 * tamanho REAL, e é ele que diz quanto ficou de fora.
 */
export function parteDoResultadoBruto(parte) {
  const props = propsDaParte(parte)
  return {
    ...parte,
    ui: {
      component: CHAVE_RESULTADO_BRUTO,
      props: {
        titulo: parte.label || parte.tool || 'Resultado da consulta',
        bytes: props.bytes,
        trecho: typeof props.preview === 'string' ? props.preview : '',
      },
    },
  }
}

/** O texto que o cartão de falha mostra: sempre uma frase, nunca vazio. */
export function textoDaFalha(parte) {
  const texto = String((parte && parte.erro) || '').trim()
  return PREFIXO_TOOL_FALHOU + ': ' + (texto || SEM_TEXTO_DE_ERRO)
}

/**
 * A entrada resolvida é um CARTÃO DE DADOS — tabela/gráfico/lista que o
 * `UI_REGISTRY` monta com `component`+`schema` — e não uma tool de FRONT
 * (`executor: 'client'`, ex.: `plataforma/navegar`).
 *
 * Usado pelo painel de artefato (plano `moonlit-conjuring-sun`, Fase 2b): só
 * o cartão de dados vira prévia clicável (`ArtefatoPreviewCard`); a ação de
 * front continua desenhando inline — ela não tem "abrir ao lado", o efeito
 * dela É a navegação/preenchimento em si.
 */
export function ehCartaoDeDados(entrada, parte) {
  if (!entrada || !entrada.component) return false
  if (typeof entrada.schema !== 'function') return false
  if (parte && parte.executor === EXECUTOR_CLIENTE) return false
  return true
}
